// ReviewNote — the amber "somebody has to look at this" treatment, and the one
// click that clears it.
//
// One amber for one gate. Five lanes raise the needs-review list (PO import, PO
// scan, quote-to-draft-PO, catalog import, packing list) and it is the same
// server-computed list behind each of them (po-import-service.js
// computeReviewRequired), so the treatment has to be the same too: a reviewer who
// learns "amber means look, Looks right clears it" on one screen must not have to
// relearn it on the next.
//
// The lane-specific parts stay out: each lane injects its own reason-tag copy
// through `labels`, and builds its own slots. This renders one slot, nothing more.

import React from 'react';
import { REVIEW_REASON_LABELS, type ReviewTarget } from './grounding';

/**
 * One PLACE a person must look at, as a screen sees it: whether it is still
 * open, the ring to put around its input, and the two ways to close it — type
 * in the field, or say it looks right.
 */
export interface ReviewSlot {
  key: string;
  /**
   * Typed as what this component RENDERS rather than as any one lane's review
   * entry, so a lane's wider reason union is assignable without either side
   * weakening its own types at the source. Everything here still comes from a
   * server-computed list; this is a display shape.
   */
  entries: Array<{
    target: ReviewTarget;
    field: string | null;
    line_number: number | null;
    reason: string;
    message: string;
  }>;
  open: boolean;
  label: string;
  /** Appended to an input's className while the slot is open. */
  ring: string;
  /** Called alongside any edit to that field. No-op when nothing is flagged. */
  markEdited: () => void;
  looksRight: () => void;
}

/**
 * The reason and the reader's own words are BOTH shown: a tag alone tells a
 * reviewer that something is wrong without telling them what to check, which
 * across 193 documents is how a gate turns into a button people mash.
 */
export const ReviewNote: React.FC<{
  slot: ReviewSlot;
  /**
   * Reason → tag copy. Defaults to the kit's own map, which is exhaustive over
   * the reasons the shared parser raises. Each lane that can raise more passes
   * its own superset (the quote lane adds a fee row, an unstated currency, a
   * total that does not add up, a discount read backwards), so an unmapped
   * reason never renders as an empty amber tag.
   */
  labels?: Record<string, string>;
}> = ({ slot, labels = REVIEW_REASON_LABELS }) => {
  if (slot.entries.length === 0) return null;
  if (!slot.open) {
    return (
      <span className="mt-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/10 text-green-300 ring-1 ring-inset ring-green-500/25">
        Looked at
      </span>
    );
  }
  return (
    <span className="mt-1 block space-y-1">
      {slot.entries.map((entry, i) => (
        <span key={i} className="block text-[10px] leading-snug text-amber-300/90">
          {entry.message}
        </span>
      ))}
      <span className="flex items-center gap-2 flex-wrap">
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/40">
          {labels[slot.entries[0].reason] || 'Worth a look'}
        </span>
        <button
          type="button"
          onClick={slot.looksRight}
          aria-label={`Looks right: ${slot.label}`}
          className="text-[10px] text-amber-200 underline hover:text-amber-100"
        >
          Looks right
        </button>
      </span>
    </span>
  );
};

export default ReviewNote;
